import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { AppShell } from "@/components/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, Truck, ListChecks, Search, Users, Wrench, Settings,
  Inbox, BarChart3, ScrollText, UserCog, History as HistoryIcon
} from "lucide-react";
import TruckstopDashboard from "./TruckstopDashboard";
import NewEntry from "./NewEntry";
import OpenOrders from "./OpenOrders";
import OrderDetail from "./OrderDetail";
import RegistrationSearch from "./RegistrationSearch";
import Customers from "./Customers";
import CustomerDetail from "./CustomerDetail";
import ServiceTypes from "./ServiceTypes";
import UsersPage from "./UsersPage";
import SettingsPage from "./SettingsPage";
import MessagesPage from "./MessagesPage";
import ReportsPage from "./ReportsPage";
import AuditPage from "./AuditPage";
import History from "./History";
import FinanceModule from "./FinanceModule";
import TenantBillingModule from "./TenantBillingModule";
import { CreditCard } from "lucide-react";

const STAFF = "truckstop_staff";
const OWNER = "truckstop_owner";

export default function TruckstopLayout() {
  const { user, tenant } = useAuth();
  const [unread, setUnread] = useState(0);
  const isStaff = user?.role === STAFF;
  const isOwner = user?.role === OWNER;
  const finOn = !!tenant?.feature_flags?.financial;

  useEffect(() => {
    if (!isStaff) {
      api.get("/messages/unread-count").then(({ data }) => setUnread(data.count || 0)).catch(() => {});
    }
  }, [isStaff]);

  const sections = isStaff ? [
    {
      label: "Operations",
      items: [
        { to: "/app", end: true, label: "Dashboard", icon: <LayoutDashboard size={16} />, testId: "nav-dashboard" },
        { to: "/app/new-entry", label: "New Truck Entry", icon: <Truck size={16} />, testId: "nav-new-entry" },
        { to: "/app/open-orders", label: "Open Orders", icon: <ListChecks size={16} />, testId: "nav-open-orders" },
        { to: "/app/search", label: "Registration Search", icon: <Search size={16} />, testId: "nav-search" },
      ],
    },
  ] : [
    {
      label: "Operations",
      items: [
        { to: "/app", end: true, label: "Dashboard", icon: <LayoutDashboard size={16} />, testId: "nav-dashboard" },
        { to: "/app/new-entry", label: "New Truck Entry", icon: <Truck size={16} />, testId: "nav-new-entry" },
        { to: "/app/open-orders", label: "Open Orders", icon: <ListChecks size={16} />, testId: "nav-open-orders" },
        { to: "/app/history", label: "History", icon: <HistoryIcon size={16} />, testId: "nav-history" },
        { to: "/app/search", label: "Registration Search", icon: <Search size={16} />, testId: "nav-search" },
      ],
    },
    {
      label: "Management",
      items: [
        { to: "/app/customers", label: "Customers", icon: <Users size={16} />, testId: "nav-customers" },
        { to: "/app/service-types", label: "Service Types", icon: <Wrench size={16} />, testId: "nav-service-types" },
        { to: "/app/users", label: "Users", icon: <UserCog size={16} />, testId: "nav-users" },
        { to: "/app/reports", label: "Reports", icon: <BarChart3 size={16} />, testId: "nav-reports" },
        { to: "/app/audit", label: "Audit Log", icon: <ScrollText size={16} />, testId: "nav-audit" },
      ],
    },
    {
      label: "Account",
      items: [
        { to: "/app/settings", label: "Settings", icon: <Settings size={16} />, testId: "nav-settings" },
        { to: "/app/messages", label: "Messages", icon: <Inbox size={16} />, testId: "nav-messages", badge: unread },
        ...(isOwner ? [{ to: "/app/billing", label: "Billing", icon: <CreditCard size={16} />, testId: "nav-billing" }] : []),
      ],
    },
    ...(finOn ? [{
      label: "Finance",
      items: [
        { to: "/app/finance", end: true, label: "Finance", icon: <BarChart3 size={16} />, testId: "nav-finance" },
        { to: "/app/finance/uninvoiced-charges", label: "Uninvoiced Charges", icon: <ScrollText size={16} />, testId: "nav-finance-uic" },
        { to: "/app/finance/rates", label: "Rates", icon: <ScrollText size={16} />, testId: "nav-finance-rates" },
        { to: "/app/finance/opening", label: "Opening Balances", icon: <ScrollText size={16} />, testId: "nav-finance-opening" },
        { to: "/app/finance/invoices", label: "Invoices", icon: <ScrollText size={16} />, testId: "nav-finance-invoices" },
        { to: "/app/finance/credit-notes", label: "Credit Notes", icon: <ScrollText size={16} />, testId: "nav-finance-cn" },
        { to: "/app/finance/payments", label: "Payments", icon: <ScrollText size={16} />, testId: "nav-finance-payments" },
        { to: "/app/finance/statements", label: "Statements", icon: <ScrollText size={16} />, testId: "nav-finance-statements" },
        { to: "/app/finance/branding", label: "Branding", icon: <Settings size={16} />, testId: "nav-finance-branding" },
      ],
    }] : []),
  ];

  return (
    <AppShell
      brand={{ title: "Truck Stop", subtitle: "Operations" }}
      sections={sections}
      testIdPrefix="app"
    >
      <Routes>
        <Route index element={<TruckstopDashboard />} />
        <Route path="new-entry" element={<NewEntry />} />
        <Route path="open-orders" element={<OpenOrders />} />
        <Route path="orders/:id" element={<OrderDetail />} />
        <Route path="search" element={<RegistrationSearch />} />
        {!isStaff && <Route path="history" element={<History />} />}
        {!isStaff && <Route path="customers" element={<Customers />} />}
        {!isStaff && <Route path="customers/:id" element={<CustomerDetail />} />}
        {!isStaff && <Route path="service-types" element={<ServiceTypes />} />}
        {!isStaff && <Route path="users" element={<UsersPage />} />}
        {!isStaff && <Route path="reports" element={<ReportsPage />} />}
        {!isStaff && <Route path="audit" element={<AuditPage />} />}
        {!isStaff && <Route path="settings" element={<SettingsPage />} />}
        {!isStaff && <Route path="messages" element={<MessagesPage />} />}
        {!isStaff && finOn && <Route path="finance/*" element={<FinanceModule />} />}
        {isOwner && <Route path="billing/*" element={<TenantBillingModule />} />}
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
    </AppShell>
  );
}
